import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { useTheme } from '@emotion/react';

import { outputIfNoVariables } from '../../utilities/misc';
import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { containerStyles } from '../../styles/layout';
import { headers, body, richTextStyles } from '../../styles/type';
import { dotPatternStyles } from '../../styles/backgrounds';
import { shadow } from '../../styles/box';

import Background from '../Background';
import Button from '../buttons/Button';
import ButtonGroup from '../ButtonGroup';
import ButtonText from '../buttons/ButtonText';
import TextRenderer from '../TextRenderer';
import VideoPlayer from '../VideoPlayer';

const StyledBackground = styled(Background)`
  padding: ${rem(32)} 0;
  ${media('>tiny')} {
    padding: ${rem(48)} 0;
  }

  &::before {
    ${dotPatternStyles}
    ${({ pattern }) => pattern && `
      display: none;
    `}
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: ${rem(180)};
    height: ${rem(56)};
  }
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;

  ${media('>small')} {
    flex-direction: row-reverse;
  }
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${media('>small')} {
    flex: 0 1 ${rem(640)};
    margin-right: auto;
    padding-right: ${rem(24)};

    ${({ small }) => small && `
      flex: 0 1 ${rem(570)};
    `}
  }

  ${ButtonGroup} {
    margin-top: ${rem(16)};
  }
`;

const EyebrowSubhead = styled.p`
  ${headers.eyebrow}
  position: relative;
  display: inline-block;
  margin-bottom: ${rem(12)};
  color: ${colors.blue['500']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['200']};
  `}

  &::before {
    ${dotPatternStyles}
    ${({ backgroundPattern }) => backgroundPattern && `
      display: none;
    `}
    content: '';
    position: absolute;
    bottom: 100%;
    margin-bottom: ${rem(24)};
    left: 0;
    width: ${rem(180)};
    height: ${rem(112)};
  }
`;

const Headline = styled.h1`
  ${headers.h2}
  margin-bottom: ${rem(24)};
  color: ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.white : colors.blue['800'])};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}
`;

const Subhead = styled.p`
  ${body.regular}
  color: ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.white : colors.gray['600'])};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.gray['400']};
  `}
`;

const FeaturedImage = styled(GatsbyImage)`
  margin-bottom: ${rem(24)};

  ${({ imagesize }) => (imagesize === 'large' ? `
    position: relative;
    max-width: ${rem(675)};
    width: 100%;
    left: ${rem(20)};
    margin-left: auto;
    margin-bottom: ${rem(24)};

    ${media('>small')} {
      left: ${rem(40)};
      margin-left: ${rem(-40)};
      top: ${rem(48)};
      margin-top: ${rem(-48)};
      align-self: flex-end;
      margin-bottom: 0;
    }

    @media (min-width: 1380px) {
      max-width: ${rem(800)};
    }

    @media (min-width: 1380px) and (max-width: 1920px) {
      left: calc((100vw - 1300px) / 2);
    }

    @media (min-width: 1920px) {
      left: 315px;
    }
  ` : `
    object-fit: cover;
    aspect-ratio: 450/300;
    max-width: ${rem(450)};
    border-radius: ${rem(10)};

    ${media('>small')} {
      flex: 0 1 ${rem(450)};
      align-self: flex-start;
      margin-bottom: 0;
    }
  `)}
`;

const Video = styled.div`
  border-radius: ${rem(10)};
  margin-bottom: ${rem(24)};
  overflow: hidden;

  ${media('>small')} {
    flex: 0 1 ${rem(720)};
    align-self: flex-start;
    margin-bottom: 0;
  }
`;

const Content = styled.div`
  ${shadow}
  ${body.regular}
  ${richTextStyles}
  border-radius: ${rem(10)};
  margin-bottom: ${rem(24)};
  max-width: 45%;
  padding: ${rem(35)};

  ${media('<=small')} {
    max-width: 100%;
  }

  .legal-consent-container .hs-richtext{
    font-size: ${rem(18)};
    line-height: ${rem(18)};
  }
`;

const Annotation = styled.div`
  margin-top: ${rem(18)};
`;

const AnnotationImage = styled(GatsbyImage)`
  margin-top: ${rem(16)};
  max-width: ${rem(600)};
`;

const Header = ({
  useH1,
  headline,
  eyebrowSubhead,
  subhead,
  image,
  largeImage,
  video,
  annotationImage,
  text,
  primaryCtaText,
  primaryCtaLink,
  secondaryCtaText,
  secondaryCtaLink,
  backgroundColor = 'White',
  backgroundPattern
}) => {
  const { pageOverrides } = useTheme();
  return (
    <StyledBackground
      color={backgroundColor}
      pattern={backgroundPattern}
      variation={backgroundPattern === 'Large Dots' ? 'short' : null}
    >
      <ContentWrapper>
        {image && !video && !text && (
          <FeaturedImage
            imagesize={largeImage ? 'large' : 'normal'}
            image={largeImage ? getImage(image.largeImage) : getImage(image.smallImage)}
            alt={image.description}
            loading="eager"
          />
        )}
        {video && !text && (
          <Video>
            <VideoPlayer
              url={video.videoUrl}
              title={video.title}
              description={video?.description?.description}
              thumbnailUrl={video.thumbnail?.file?.url}
              thumbnailImage={video.thumbnail?.gatsbyImageData}
              videoBorder={video?.videoBorder}
              uploadDate={video.createdAt}
            />
          </Video>
        )}
        {text && (
          <Content>
            <TextRenderer text={text} richText />
          </Content>
        )}
        <TextContent small={video}>
          {eyebrowSubhead && (
            <EyebrowSubhead backgroundPattern={backgroundPattern}>
              <TextRenderer text={eyebrowSubhead} />
            </EyebrowSubhead>
          )}
          {headline && (
            <Headline as={useH1 ? 'h1' : 'h2'} backgroundColor={backgroundColor}>
              <TextRenderer text={headline} />
            </Headline>
          )}
          {subhead && (
            <Subhead backgroundColor={backgroundColor}>
              <TextRenderer text={subhead} />
            </Subhead>
          )}
          <ButtonGroup>
            {primaryCtaText && primaryCtaText && (
              <Button
                light={backgroundColor === 'Blue' && !pageOverrides?.includes('Dark Mode')}
                to={primaryCtaLink}
                aria-label={outputIfNoVariables(`${headline} — ${primaryCtaText}`)}
              >
                <TextRenderer text={primaryCtaText} />
              </Button>
            )}
            {secondaryCtaText && secondaryCtaText && (
              <ButtonText
                color={backgroundColor === 'Blue' || pageOverrides?.includes('Dark Mode') ? 'light' : 'dark'}
                to={secondaryCtaLink}
                aria-label={outputIfNoVariables(`${headline} — ${secondaryCtaText}`)}
              >
                <TextRenderer text={secondaryCtaText} />
              </ButtonText>
            )}
          </ButtonGroup>
          <Annotation>
            {annotationImage && (
              <AnnotationImage
                image={getImage(annotationImage)}
                alt={annotationImage.description}
                objectFit="contain"
                objectPosition="left top"
                loading="eager"
              />
            )}
          </Annotation>
        </TextContent>
      </ContentWrapper>
    </StyledBackground>
  );
};

Header.propTypes = {
  headline: PropTypes.string,
  eyebrowSubhead: PropTypes.string,
  subhead: PropTypes.string,
  image: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    largeImage: PropTypes.object,
    // eslint-disable-next-line react/forbid-prop-types
    smallImage: PropTypes.object,
    description: PropTypes.string
  }),
  largeImage: PropTypes.bool,
  video: PropTypes.shape({
    videoUrl: PropTypes.string,
    title: PropTypes.string,
    createdAt: PropTypes.string,
    description: PropTypes.shape({
      description: PropTypes.string
    }),
    annotationImage: PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types
      gatsbyImageData: PropTypes.object
    }),
    text: PropTypes.shape({
      raw: PropTypes.string,
      // eslint-disable-next-line react/forbid-prop-types
      references: PropTypes.arrayOf(PropTypes.object)
    }),
    thumbnail: PropTypes.shape({
      url: PropTypes.string,
      // eslint-disable-next-line react/forbid-prop-types
      gatsbyImageData: PropTypes.object,
      file: PropTypes.shape({
        url: PropTypes.string
      })
    })
  }),
  backgroundColor: PropTypes.oneOf(['Blue', 'White', 'Light Blue']),
  backgroundPattern: PropTypes.oneOf(['Large Dots', 'Splash Dots', 'Splash'])
};

export const query = graphql`
  fragment HeaderFragment on ContentfulHeader {
    id
    __typename
    headline
    eyebrowSubhead
    subhead
    text {
      raw
      references {
        ...TextRendererAssetFragment
        ...TextRendererButtonFragment
        ...TextRendererFormFragment
        ...TextRendererTrialFormFragment
        ...TextRendererImageFragment
        ...TextRendererMediaFragment
        ...TextRendererVideoFragment
        ...TextRendererWidgetFragment
      }
    }
    annotationImage {
      gatsbyImageData(quality: 75)
      description
    }
    primaryCtaText
    primaryCtaLink
    secondaryCtaText
    secondaryCtaLink
    image {
      largeImage: gatsbyImageData(width: 800, quality: 75)
      smallImage: gatsbyImageData(width: 450, height: 300, quality: 75, resizingBehavior: FILL)
      description
    }
    largeImage
    video {
      videoUrl
      title
      createdAt
      description {
        description
      }
      thumbnail {
        url
        gatsbyImageData(width: 450, height: 300, quality: 75, resizingBehavior: FILL)
        file {
          url
        }
      }
    }
    backgroundColor
    backgroundPattern
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default Header;
